import clsx from 'clsx';
import { useEffect, useMemo, useRef, type PropsWithChildren } from 'react';

import { toolbarVisibilityOverlay } from './ChartToolbarRenderer.sty.js';
import type { ChartToolbarRendererProps } from './types.js';
import { useApplyCallbackOnScroll } from '../../hooks/useApplyCallbackOnScroll.js';
import { useEffectOnValueChange } from '../../hooks/useEffectOnValueChange.js';
import type { ViewportEventPosition } from '../../types/pointer-events.js';
import { isWithinBounds } from '../../utils/is-within-bounds.js';
import type { ChartLayoutDimensions } from '../chart-layout/types/chart-layout.js';
import { interactionsOverlay } from '../interactions-overlay/InteractionsOverlay.sty.js';

type ChartToolbarVisibilityOverlayProps = {
  boundSize: ChartLayoutDimensions;
  visibleWhenMouseInBounds: boolean;
  setIsVisible: (isVisible: boolean) => void;
  isVisible: boolean;
  handlerRef?: ChartToolbarRendererProps['handlerRef'];
};

export const ChartToolbarVisibilityOverlay = (
  props: PropsWithChildren<ChartToolbarVisibilityOverlayProps>,
) => {
  const { children, visibleWhenMouseInBounds } = props;

  if (!visibleWhenMouseInBounds) {
    return children;
  }

  return <VisibilityOverlay {...props} />;
};

const VisibilityOverlay = (
  props: PropsWithChildren<ChartToolbarVisibilityOverlayProps>,
) => {
  const { boundSize, children, setIsVisible, isVisible, handlerRef } = props;

  const overlayRef = useRef<HTMLDivElement>(null);

  const overlayState = useMemo(() => {
    let bounds: DOMRect | undefined;
    return {
      inBounds: (e: ViewportEventPosition) => {
        if (!bounds) {
          bounds = overlayRef.current?.getBoundingClientRect();
        }
        return isWithinBounds(e, bounds);
      },
      resetBounds: () => {
        bounds = undefined;
      },
    };
  }, []);

  useApplyCallbackOnScroll(!isVisible, () => {
    overlayState.resetBounds();
  });

  useEffectOnValueChange(() => {
    overlayState.resetBounds();
    setIsVisible(false);
  }, [boundSize.height, boundSize.width]);

  useEffect(() => {
    if (handlerRef?.current) {
      handlerRef.current.handleFocus = () => {
        setIsVisible(true);
      };
    }
    return () => {
      if (handlerRef?.current) {
        handlerRef.current.handleFocus = () => null;
      }
    };
  });

  useEffect(() => {
    const cbMouse = (event: MouseEvent) => {
      if (!overlayState.inBounds(event)) {
        setIsVisible(false);
        overlayState.resetBounds();
      }
    };

    if (isVisible) {
      document.addEventListener('mouseup', cbMouse);
      document.addEventListener('pointerout', cbMouse);
    }

    return () => {
      document.removeEventListener('mouseup', cbMouse);
      document.removeEventListener('pointerout', cbMouse);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return (
    <div
      role="none"
      ref={overlayRef}
      className={clsx(toolbarVisibilityOverlay, interactionsOverlay)}
      onPointerMove={(e: ViewportEventPosition) => {
        if (overlayState.inBounds(e)) {
          isVisible || setIsVisible(true);
        }
      }}
      onClick={() => {
        isVisible || setIsVisible(true);
      }}
    >
      {children}
    </div>
  );
};
